import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  Container,
  Modal,
  Tooltip,
  FormControl,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  InputAdornment,
} from '@mui/material';
import LoadingBar from 'react-top-loading-bar';
import LocationOnIcon from "@mui/icons-material/LocationOn";

const CreateAnnouncement = () => {
  // Branch feature states
  const [isMaster, setIsMaster] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('success');
  const [error, setError] = useState('');
  const [classOptions, setClassOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);

  // Branch fetch function - exact same as AssignmentCreateForm
  const fetchBranches = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        setBranches(data);

        // Set default branch to first one
        if (data.length > 0) {
          setSelectedBranchId(data[0].branchId);
        }
      } else {
        console.error("Failed to fetch branches");
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  // Check master user and fetch branches if needed - exact same as AssignmentCreateForm
  useEffect(() => {
    const isMasterUser = sessionStorage.getItem("is_Master") === "true";
    setIsMaster(isMasterUser);

    if (isMasterUser) {
      fetchBranches();
    } else {
      const branchId = sessionStorage.getItem("branchId");
      setSelectedBranchId(branchId);
    }
  }, []);

  // Fetch user options with Branch_Id query parameter - FIXED
  useEffect(() => {
    const fetchUserOptions = async () => {
      if (!selectedBranchId) return;

      try {
        setLoadingBarProgress(30);
        const token = sessionStorage.getItem('token');
        const apiUrl = process.env.REACT_APP_BASE_URL;

        // IMPORTANT: Adding Branch_Id as query parameter like AssignmentCreateForm
        const response = await fetch(`${apiUrl}/Anouncement/GetUserGroup?Branch_Id=${selectedBranchId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({
            teacherId: sessionStorage.getItem('employeeId'),
          }),
        });

        const data = await response.json();
        const options = data.list.map((user) => ({
          value: user.userGroupId,
          label: user.userGroupname,
        }));
        setUserOptions(options);
        setLoadingBarProgress(100);
      } catch (error) {
        console.error('Error fetching user options:', error);
        setLoadingBarProgress(0);
      }
    };

    if (selectedBranchId) {
      fetchUserOptions();
    }
  }, [selectedBranchId]);

  // Fetch classes with Branch_Id query parameter - FIXED
  useEffect(() => {
    const fetchClassOptions = async () => {
      if (!selectedBranchId) return;

      try {
        setLoadingBarProgress(30);
        const token = sessionStorage.getItem('token');
        const apiUrl = process.env.REACT_APP_BASE_URL;

        // IMPORTANT: Adding Branch_Id as query parameter like AssignmentCreateForm
        const response = await fetch(`${apiUrl}/CreateClass/GetClassName?Branch_Id=${selectedBranchId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({}),
        });

        const data = await response.json();
        const options = data.map((cls) => ({
          value: cls.classId,
          label: cls.className,
        }));
        setClassOptions(options);
        setLoadingBarProgress(100);
      } catch (error) {
        console.error('Error fetching class options:', error);
        setLoadingBarProgress(0);
      }
    };

    if (selectedBranchId) {
      fetchClassOptions();
    }
  }, [selectedBranchId]);

  // Fetch sections with Branch_Id query parameter - FIXED
  useEffect(() => {
    const fetchSectionOptions = async () => {
      if (selectedClasses.length > 0 && selectedBranchId) {
        const classId = selectedClasses[selectedClasses.length - 1].value;
        try {
          setLoadingBarProgress(30);
          const token = sessionStorage.getItem('token');
          const apiUrl = process.env.REACT_APP_BASE_URL;

          // IMPORTANT: Adding Branch_Id as query parameter like AssignmentCreateForm
          const response = await fetch(`${apiUrl}/Exam/ddlSection_clsId?Branch_Id=${selectedBranchId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: token,
            },
            body: JSON.stringify({ classId }),
          });

          const data = await response.json();
          const options = data.map((section) => ({
            value: section.sectionId,
            label: section.sectionName,
          }));
          setSectionOptions(options);
          setLoadingBarProgress(100);
        } catch (error) {
          console.error('Error fetching section options:', error);
          setLoadingBarProgress(0);
        }
      } else {
        setSectionOptions([]);
      }
    };

    if (selectedBranchId) {
      fetchSectionOptions();
    }
  }, [selectedClasses, selectedBranchId]);

  // Handle Save with Branch_Id
  const handleSave = async () => {
    if (
      selectedUsers.length === 0 ||
      selectedClasses.length === 0 ||
      !selectedSection ||
      title.trim() === '' ||
      message.trim() === ''
    ) {
      setError('Please fill in all required fields.');
      return;
    }

    const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
    const token = sessionStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_BASE_URL;

    const payload = {
      userGroupId: selectedUsers.map((user) => user.value),
      classId: selectedClasses.map((cls) => cls.value),
      sectionId: selectedSection.value,
      message: message.trim(),
      title: title.trim(),
    };

    try {
      setLoadingBarProgress(30);
      // IMPORTANT: Adding Branch_Id as query parameter
      const response = await fetch(`${apiUrl}/Anouncement/CreateAnouncement?Branch_Id=${branchId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to create announcement');
      }

      setModalType('success');
      setModalMessage('Announcement Saved Successfully!');
      setModalOpen(true);
      setError('');
      setLoadingBarProgress(100);
      handleCancel();
    } catch (error) {
      console.error('Error creating announcement:', error);
      setModalType('error');
      setModalMessage('Failed to create announcement. Please try again.');
      setModalOpen(true);
      setLoadingBarProgress(0);
    }
  };

  const handleCancel = () => {
    setSelectedUsers([]);
    setSelectedClasses([]);
    setSelectedSection(null);
    setMessage('');
    setTitle('');
    setError('');
  };

  return (
    <Container sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <LoadingBar color="#0B1F3D" progress={loadingBarProgress} onLoaderFinished={() => setLoadingBarProgress(0)} />

      {/* AppBar with Branch Dropdown - exact same styling as AssignmentCreateForm */}
      <AppBar position="static" sx={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
            Create Announcement
          </Typography>

          {/* Branch Dropdown - Only for Master Users */}
          {isMaster && (
            <Box sx={{ position: "relative" }}>
              <FormControl size="small" sx={{ width: 220 }}>
                <InputLabel
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                    top: "-2px",
                    "&.Mui-focused": { color: "#64B5F6" },
                    "&.MuiFormLabel-filled": { color: "rgba(255,255,255,0.7)" },
                  }}
                >
                  BRANCH
                </InputLabel>
                <MuiSelect
                  value={selectedBranchId || ""}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                  label="BRANCH"
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.5)", ml: 0.5 }} />
                    </InputAdornment>
                  }
                  renderValue={(selected) => {
                    const branch = branches.find(b => b.branchId === selected);
                    return branch?.branchName || '';
                  }}
                  sx={{
                    color: "#ffffff",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    letterSpacing: "0.3px",
                    backgroundColor: "rgba(255,255,255,0.07)",
                    backdropFilter: "blur(8px)",
                    borderRadius: "8px",
                    transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.15)",
                      borderWidth: "1px",
                    },
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.11)",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255,255,255,0.35)",
                      },
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                      boxShadow: "0 0 0 3px rgba(100,181,246,0.18)",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#64B5F6",
                        borderWidth: "1.5px",
                      },
                    },
                    "& .MuiSelect-icon": {
                      color: "rgba(255,255,255,0.5)",
                    },
                    "&.Mui-focused .MuiSelect-icon": {
                      color: "#64B5F6",
                    },
                    "& .MuiSelect-select": {
                      py: 1.1,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: "10px",
                        border: "1px solid rgba(11,31,61,0.12)",
                        boxShadow: "0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)",
                        overflow: "hidden",
                        "& .MuiList-root": { py: 0.5 },
                      },
                    },
                  }}
                >
                  {branches.map((branch) => (
                    <MenuItem
                      key={branch.branchId}
                      value={branch.branchId}
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "#0B1F3D",
                        px: 2,
                        py: 1.1,
                        mx: 0.5,
                        borderRadius: "6px",
                        "&:hover": {
                          backgroundColor: "rgba(11,31,61,0.07)",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "rgba(25,118,210,0.1)",
                          color: "#1565C0",
                          fontWeight: 600,
                        },
                      }}
                    >
                      {branch.branchName}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Rest of the form - exactly as before */}
      <Paper elevation={3} style={{ padding: 24, width: '100%', margin: 'auto', marginTop: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Select User(s) *
          </Typography>
          <Select
            isMulti
            options={userOptions}
            value={selectedUsers}
            onChange={setSelectedUsers}
            placeholder="Select user(s)"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: 56,
                borderRadius: 8,
                borderColor: '#ccc',
              }),
            }}
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Select Class(es) *
          </Typography>
          <Select
            isMulti
            options={classOptions}
            value={selectedClasses}
            onChange={setSelectedClasses}
            placeholder="Select class(es)"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: 56,
                borderRadius: 8,
                borderColor: '#ccc',
              }),
            }}
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Select Section *
          </Typography>
          <Select
            options={sectionOptions}
            value={selectedSection}
            onChange={setSelectedSection}
            placeholder="Select section"
            isDisabled={selectedClasses.length === 0}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: 56,
                borderRadius: 8,
                borderColor: '#ccc',
              }),
            }}
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Title *
          </Typography>
          <TextField
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter announcement title"
            variant="outlined"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Message *
          </Typography>
          <Box
            sx={{
              '& .ql-container': { minHeight: 200, borderRadius: 2 },
              '& .ql-toolbar': { borderRadius: '8px 8px 0 0' },
              '& .ql-editor': { minHeight: 180 },
            }}
          >
            <ReactQuill
              value={message}
              onChange={setMessage}
              placeholder="Enter announcement message..."
              theme="snow"
            />
          </Box>
        </Box>
        {error && (
          <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>
            {error}
          </Typography>
        )}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 3 }}>
          <Tooltip
            title={
              !selectedUsers.length
                ? "Select User(s)"
                : !selectedClasses.length
                  ? "Select Class(es)"
                  : !selectedSection
                    ? "Select Section"
                    : !title.trim()
                      ? "Enter Title"
                      : !message.trim()
                        ? "Enter Message"
                        : ""
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                sx={{ minWidth: 220 }}
                disabled={
                  !selectedUsers.length ||
                  !selectedClasses.length ||
                  !selectedSection ||
                  !title.trim() ||
                  !message.trim()
                }
              >
                Save Announcement
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
            sx={{ minWidth: 220 }}
          >
            Cancel
          </Button>
        </Box>
      </Paper>

      {/* Success/Error Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="response-modal-title"
        aria-describedby="response-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: modalType === 'success' ? '#0B1F40' : '#d32f2f',
              padding: '12px 16px',
            }}
          >
            <Typography id="response-modal-title" variant="h6" sx={{ color: '#fff' }}>
              {modalType === 'success' ? 'Success ✅' : 'Error ❌'}
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="response-modal-description" sx={{ mb: 2 }}>
              {modalMessage}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => setModalOpen(false)}
                variant="contained"
                color={modalType === 'success' ? 'primary' : 'error'}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default CreateAnnouncement;